import { OrderRestInterface } from './interfaces/_order.rest';
import { CTAPIConfiguration } from './utils/configuration';
import { RequestHelper } from './utils/request';
import { Order } from '../models/interfaces/_order.model';
import { OrderLists } from '../models/interfaces/_order-lists.model';
import { OrderFilter } from '../models/interfaces/_order-filter.model';
import { OrderHelper } from '../models/helpers/order.helper';
import { AcceptQuote } from '../models/interfaces/_quote.model';
import { OrdersFiltered } from '../models/interfaces/_order-filtered.model';

import { Observable } from 'rxjs';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';

export class OrderRest implements OrderRestInterface {

    private configuration: CTAPIConfiguration;
    private requestHelper: RequestHelper;

    constructor(configuration: CTAPIConfiguration) {
        this.configuration = configuration;
        this.requestHelper = new RequestHelper(configuration);
    }

    /**
     * Request for active and finished orders.
     */
    getOrders(nAcctID: number, nDivID: number): Observable<OrderLists> {
        return this.requestHelper.request({ data: { nAcctID, nDivID } }, this.configuration.order.list,
            this.configuration.httpClient).pipe(
                map((response) => {
                    const orderHelper: OrderHelper = new OrderHelper();
                    return orderHelper.simplifyOrders(response);
                })
            );
    }

    /**
     * Request for order by id.
     */
    getOrder(nAcctID: number, nSalesOrderID: number, nDivID: number): Observable<Order> {
        return this.requestHelper.request({ data: { nAcctID, nSalesOrderID, nDivID } }, this.configuration.order.get,
            this.configuration.httpClient).pipe(
                map((response) => {
                    const orderHelper: OrderHelper = new OrderHelper();
                    return orderHelper.parseCTOrder(response);
                })
            );
    }

    /**
     * Request for filtered active and finished orders.
     */
    filterOrders(filter: OrderFilter): Observable<OrdersFiltered> {
        return this.requestHelper.request({ data: filter }, this.configuration.order.filter,
            this.configuration.httpClient).pipe(
                map((response) => {
                    const orderHelper: OrderHelper = new OrderHelper();
                    const ordersFiltered: OrdersFiltered = response.result;
                    const parsedOrders = [];

                    ordersFiltered.orders.forEach((order) => {
                        parsedOrders.push(orderHelper.parseCTOrder(order));
                    });

                    ordersFiltered.orders = parsedOrders;

                    return ordersFiltered;
                })
            );
    }

    /**
     * Request for creation of new order.
     */
    createOrder(order): Observable<Order> {
        return this.requestHelper.request({ data: order }, this.configuration.order.create,
            this.configuration.httpClient).pipe(
                map((response) => {
                    const orderHelper: OrderHelper = new OrderHelper();
                    return orderHelper.parseCTOrder(response.result.order);
                })
            );
    }

    /**
     * Request for creation of new order.
     */
    createManyMatOrders(order): Observable<any> {
        return this.requestHelper.request({ data: order }, this.configuration.order.createManyMatOrders,
            this.configuration.httpClient).pipe(map((response) => {
                const orderHelper: OrderHelper = new OrderHelper();
                return orderHelper.parseCTOrder(response.result.order);
            }));
    }

    /**
     * Request for an order by text code.
     */
    searchOrder(sSearchText: string, nDivID: number, environment: string, forceAnonymous: boolean = false): Observable<Order> {
        const orderSearchEndpointConfig = forceAnonymous ? this.configuration.orderAnonymous.search : this.configuration.order.search;

        return this.requestHelper.request({ data: { sSearchText, nDivID, sEnvCode: environment  } }, orderSearchEndpointConfig,
            this.configuration.httpClient).pipe(
                map((response) => {
                    const orderHelper: OrderHelper = new OrderHelper();
                    return orderHelper.parseCTOrder(response.result);
                })
            );
    }

    /**
     * Request to accept an order quotation.
     */
    acceptOrderQuote(data: AcceptQuote): Observable<any> {
        return this.requestHelper.request({ data }, this.configuration.order.acceptQuote,
            this.configuration.httpClient).pipe(
                map((response) => {
                    return response;
                })
            );
    }

    /**
     * Request to reject an order quotation.
     */
    rejectOrderQuote(data: AcceptQuote): Observable<any> {
        return this.requestHelper.request({ data }, this.configuration.order.rejectQuote,
            this.configuration.httpClient).pipe(
                map((response) => {
                    return response;
                })
            );
    }

    /**
     * Request for order map data (ex: route).
     */
    getOrderMap(data: any, forceAnonymous: boolean = false): Observable<Order> {
        const orderMapInfoEndpointConfig = forceAnonymous ? this.configuration.orderAnonymous.mapInfo : this.configuration.order.mapInfo;

        return from(this.requestHelper.request({ data }, orderMapInfoEndpointConfig,
            this.configuration.httpClient).pipe(
                map((response) => {
                    return response.result;
                }))
        );
    }

}
